import { DataType, TableName } from '../../../tables'
import { Params } from '../../../state/reducers/types'
import { useDefaultDbActions } from './defaultDbActions'
import { useFindByIds, useFindReducerRemover, useFindReducerUpdater } from '../../../state/reducers/hooks'
import { DynamoApi } from '../../../services/dynamo'

export const useDefaultModuleActions = () => {
    const defaultDbActions = useDefaultDbActions()
    const findReducerUpdater = useFindReducerUpdater()
    const findReducerRemover = useFindReducerRemover()
    const findByIds = useFindByIds()

    const getUpdater = (tableName: TableName) => findReducerUpdater(tableName) as any
    const getRemover = (tableName: TableName) => findReducerRemover(tableName) as any

    return (dynamoApi: DynamoApi, companyId?: string) => {
        const fetchAll = async (tableName: TableName) => {
            return defaultDbActions(dynamoApi, companyId).getAll(tableName)
                .then(dataTypes => {
                    dataTypes.length > 0 && getUpdater(tableName)(dataTypes)
                    return dataTypes
                })
        }

        const fetchByIds = async (
            tableName: TableName,
            ids: string[]
        ): Promise<DataType[]> => {
            if (ids.length > 0) {
                const list = findByIds(tableName, ids)
                if (list.length === ids.length) {
                    return list
                } else {
                    return defaultDbActions(dynamoApi, companyId).getByIds(tableName, ids)
                        .then(dataTypes => {
                            dataTypes.length > 0 && getUpdater(tableName)(dataTypes)
                            return dataTypes
                        })
                }
            } else {
                return []
            }
        }

        const count = async (
            tableName: TableName,
            startKey?: Params,
            limit?: number,
            propFilters?: Params
        ): Promise<number> => {
            return defaultDbActions(dynamoApi, companyId).count(tableName, startKey, limit, propFilters)
        }
        
        const save = async (
            tableName: TableName,
            dataType: DataType
        ): Promise<DataType> => {
            const dataTypes = [dataType]
            return defaultDbActions(dynamoApi, companyId).save(tableName, dataTypes)
                .then(_ => {
                    getUpdater(tableName)(dataTypes)
                    return dataType
                })
        }

        const saveMultiple = async (
            tableName: TableName,
            dataTypes: DataType[]
        ): Promise<DataType[]> => {
            if (dataTypes.length > 0) {
                return defaultDbActions(dynamoApi, companyId).save(tableName, dataTypes)
                    .then(_ => {
                        getUpdater(tableName)(dataTypes)
                        return dataTypes
                    })
            } else {
                return new Promise(resolve => resolve([]))
            }
        }

        const remove = async (
            tableName: TableName,
            id: string
        ): Promise<void> => {
            const ids = [id]
            return defaultDbActions(dynamoApi, companyId).remove(tableName, ids)
                .then(() => getRemover(tableName)(ids))
        }

        const removeMultiple = async (
            tableName: TableName,
            ids: string[]
        ): Promise<void> => {
            if (ids.length > 0) {
                return defaultDbActions(dynamoApi, companyId).remove(tableName, ids)
                    .then(_ => getRemover(tableName)(ids))    
            } else {
                return new Promise(resolve => resolve())
            }
        }

        return {
            fetchAll,
            fetchByIds,
            count,
            save,
            saveMultiple,
            remove,
            removeMultiple
        }
    }
}
